/** Up to two uppercased word-initials; '?' when empty. */
export function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "HH:mm" (24h) → "10:30 AM". */
export function formatClock(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const d = new Date(); d.setHours(h ?? 0, m ?? 0);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Recurring slots → "Mon & Fri · 10:30 AM" (groups days that share a time). */
export function formatSchedule(slots: { weekday: number; time: string }[]): string {
  if (!slots.length) return '';
  const byTime = new Map<string, number[]>();
  for (const s of slots) {
    const days = byTime.get(s.time) ?? [];
    if (!days.includes(s.weekday)) days.push(s.weekday);
    byTime.set(s.time, days);
  }
  return [...byTime.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([time, days]) => {
      const labels = days.sort((a, b) => a - b).map((d) => WEEKDAY_SHORT[d]);
      const daysStr = labels.length <= 2 ? labels.join(' & ') : `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
      return `${daysStr} · ${formatClock(time)}`;
    })
    .join(', ');
}

/** Pull a goal weight out of freeform objectives text, e.g. "get to 170 lb" → 170.
 *  Looks for a number immediately followed by lb/lbs/pound(s). null if none. */
export function parseGoalWeight(objectives: string): number | null {
  const m = (objectives || '').match(/(\d{2,3})\s*(?:lb|lbs|pound)/i);
  return m ? Number(m[1]) : null;
}

/** ISO date (YYYY-MM-DD) → "Mon, May 19". Falls back to the raw string. */
export function formatDay(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}
